/**
 * Mock Analytics Data
 * Comprehensive analytics data for dashboard visualizations
 */

export interface TeamPerformanceMetrics {
  totalTasks: number;
  completedTasks: number;
  inProgressTasks: number;
  pendingTasks: number;
  completionRate: number;
  averageTimePerTask: number; // in hours
  teamVelocity: number; // story points per sprint
  teamMembers: number;
}

export interface SprintVelocityData {
  sprintName: string;
  plannedPoints: number;
  completedPoints: number;
  carryOverPoints: number;
}

export interface TaskCompletionData {
  date: string;
  completed: number;
  created: number;
  inProgress: number;
}

export interface TimeTrackingAnalytics {
  totalHoursLogged: number;
  averageHoursPerDay: number;
  billableHours: number;
  nonBillableHours: number;
  overtimeHours: number;
  hoursBreakdown: {
    development: number;
    meetings: number;
    testing: number;
    planning: number;
    review: number;
  };
}

export interface BurndownData {
  day: number;
  ideal: number;
  actual: number;
  remaining: number;
}

export interface ProductivityInsights {
  peakProductivityHours: string;
  mostProductiveDay: string;
  averageTaskCompletionTime: number;
  focusTimePercentage: number;
  meetingTimePercentage: number;
  blockerResolutionTime: number; // in hours
}

export interface TeamMemberPerformance {
  name: string;
  tasksCompleted: number;
  hoursLogged: number;
  velocity: number;
  efficiency: number; // percentage
}

// Team Performance Metrics
export const teamPerformanceMetrics: TeamPerformanceMetrics = {
  totalTasks: 145,
  completedTasks: 98,
  inProgressTasks: 32,
  pendingTasks: 15,
  completionRate: 67.6,
  averageTimePerTask: 5.4,
  teamVelocity: 42,
  teamMembers: 8,
};

// Sprint Velocity Trends (Last 6 sprints)
export const sprintVelocityData: SprintVelocityData[] = [
  {
    sprintName: 'Sprint 1',
    plannedPoints: 35,
    completedPoints: 28,
    carryOverPoints: 7,
  },
  {
    sprintName: 'Sprint 2',
    plannedPoints: 40,
    completedPoints: 38,
    carryOverPoints: 2,
  },
  {
    sprintName: 'Sprint 3',
    plannedPoints: 42,
    completedPoints: 42,
    carryOverPoints: 0,
  },
  {
    sprintName: 'Sprint 4',
    plannedPoints: 45,
    completedPoints: 40,
    carryOverPoints: 5,
  },
  {
    sprintName: 'Sprint 5',
    plannedPoints: 48,
    completedPoints: 45,
    carryOverPoints: 3,
  },
  {
    sprintName: 'Sprint 6',
    plannedPoints: 50,
    completedPoints: 47,
    carryOverPoints: 3,
  },
];

// Task Completion Rates (Last 30 days)
export const taskCompletionData: TaskCompletionData[] = [
  { date: '10/01', completed: 5, created: 7, inProgress: 3 },
  { date: '10/03', completed: 8, created: 5, inProgress: 2 },
  { date: '10/05', completed: 6, created: 8, inProgress: 4 },
  { date: '10/07', completed: 10, created: 6, inProgress: 3 },
  { date: '10/09', completed: 7, created: 9, inProgress: 5 },
  { date: '10/11', completed: 12, created: 7, inProgress: 2 },
  { date: '10/13', completed: 9, created: 10, inProgress: 4 },
  { date: '10/15', completed: 11, created: 8, inProgress: 3 },
  { date: '10/17', completed: 8, created: 6, inProgress: 4 },
  { date: '10/19', completed: 13, created: 9, inProgress: 2 },
  { date: '10/21', completed: 10, created: 11, inProgress: 5 },
  { date: '10/23', completed: 9, created: 7, inProgress: 3 },
  { date: '10/25', completed: 14, created: 8, inProgress: 2 },
  { date: '10/27', completed: 11, created: 10, inProgress: 4 },
];

// Time Tracking Analytics
export const timeTrackingAnalytics: TimeTrackingAnalytics = {
  totalHoursLogged: 1248,
  averageHoursPerDay: 6.8,
  billableHours: 936,
  nonBillableHours: 312,
  overtimeHours: 48,
  hoursBreakdown: {
    development: 650,
    meetings: 210,
    testing: 180,
    planning: 120,
    review: 88,
  },
};

// Burndown Chart Data (Current Sprint - 14 days)
export const burndownData: BurndownData[] = [
  { day: 1, ideal: 100, actual: 100, remaining: 100 },
  { day: 2, ideal: 92.8, actual: 95, remaining: 95 },
  { day: 3, ideal: 85.6, actual: 88, remaining: 88 },
  { day: 4, ideal: 78.4, actual: 82, remaining: 82 },
  { day: 5, ideal: 71.2, actual: 75, remaining: 75 },
  { day: 6, ideal: 64, actual: 68, remaining: 68 },
  { day: 7, ideal: 56.8, actual: 60, remaining: 60 },
  { day: 8, ideal: 49.6, actual: 52, remaining: 52 },
  { day: 9, ideal: 42.4, actual: 45, remaining: 45 },
  { day: 10, ideal: 35.2, actual: 38, remaining: 38 },
  { day: 11, ideal: 28, actual: 30, remaining: 30 },
  { day: 12, ideal: 20.8, actual: 22, remaining: 22 },
  { day: 13, ideal: 13.6, actual: 15, remaining: 15 },
  { day: 14, ideal: 6.4, actual: 8, remaining: 8 },
];

// Productivity Insights
export const productivityInsights: ProductivityInsights = {
  peakProductivityHours: '10:00 AM - 12:00 PM',
  mostProductiveDay: 'Tuesday',
  averageTaskCompletionTime: 5.4,
  focusTimePercentage: 68,
  meetingTimePercentage: 17,
  blockerResolutionTime: 3.2,
};

// Team Member Performance (Top 5)
export const teamMemberPerformance: TeamMemberPerformance[] = [
  {
    name: 'John Smith',
    tasksCompleted: 24,
    hoursLogged: 156,
    velocity: 8.5,
    efficiency: 92,
  },
  {
    name: 'Sarah Johnson',
    tasksCompleted: 22,
    hoursLogged: 148,
    velocity: 8.2,
    efficiency: 89,
  },
  {
    name: 'Bob Wilson',
    tasksCompleted: 20,
    hoursLogged: 145,
    velocity: 7.8,
    efficiency: 87,
  },
  {
    name: 'Emily Davis',
    tasksCompleted: 19,
    hoursLogged: 142,
    velocity: 7.5,
    efficiency: 85,
  },
  {
    name: 'Michael Chen',
    tasksCompleted: 18,
    hoursLogged: 138,
    velocity: 7.2,
    efficiency: 83,
  },
];

// Weekly time tracking trend
export const weeklyTimeData = [
  { day: 'Mon', hours: 7.2, tasks: 3 },
  { day: 'Tue', hours: 8.1, tasks: 4 },
  { day: 'Wed', hours: 7.8, tasks: 3 },
  { day: 'Thu', hours: 8.5, tasks: 5 },
  { day: 'Fri', hours: 7.5, tasks: 3 },
  { day: 'Sat', hours: 3.2, tasks: 1 },
  { day: 'Sun', hours: 2.0, tasks: 1 },
];
